/* eslint-disable no-restricted-syntax -- this line was auto generated, hence fix the issue timely */
import React, { useEffect, useCallback } from "react";

import useGlobalContext from "../../../hooks/useGlobalContext";
import useTrackerContext from "../../../hooks/useTrackerContext";
import useRouteName from "../../../hooks/useRouteName";
import useClientMetrics, { initClientMetrics } from "../../../hooks/useClientMetrics";
import useEvents from "../../../hooks/useEvents";

import {
  initializePerformanceObserver,
  Metric as ClassicMetric,
  ReportHandler as ClassicMetricReportHandler
} from "../../../utils/client-performance-metrics";

import { initETTracker } from "../../../utils/et";
import NoSSR from "../../../app/NoSSR";

const debug = require("debug")("trackers");

//
// Trackers
// --------
//
// proxy third-party client api methods through postMessage
//
// 1. Render iframe containing Riskify client script
// 2. The iframe needs to skip SSR so we can capture the onload event
// 3. The riskified loader inside is lazy-initialized on checkout page only
// 4. The last useEffect here is the only thing that re-runs, when the path in the router changes
//
// #overengineering
//
// Riskified: https://apiref.riskified.com/curl/#beacon
//

const Trackers: React.FunctionComponent = () => {
  const { payerId } = useGlobalContext();
  const { trackExternal } = useTrackerContext();
  const { trackClientMetric } = useClientMetrics();

  const currentRoute = useRouteName();
  const { jsError } = useEvents();
  const page = currentRoute || "unknown_page";

  const sendClassicMetricsToClientMetrics: ClassicMetricReportHandler = useCallback(
    (props: ClassicMetric) => {
      const { name, value } = props;
      debug(name, value);
      trackClientMetric(name, value);
    },
    [trackClientMetric]
  );

  useEffect(() => {
    initializePerformanceObserver(sendClassicMetricsToClientMetrics);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    debug("Tracking Pageview: " + page);
    trackClientMetric("pageview");
  }, [page, trackClientMetric]);

  useEffect(() => {
    debug("Running Riskified page tracking...", page);

    // track riskified on all checkout routes
    switch (page) {
      case "checkout-pax":
      case "checkout-extras":
      case "checkout-payment":
        if (payerId) {
          trackExternal({ type: "RISKX", action: "go", args: [page], data: { sessionId: payerId } });
        } else {
          jsError(new Error("Trackers: missing payerId"));
        }
        break;
    }
  }, [page, payerId, trackExternal, jsError]);

  useEffect(initClientMetrics, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(initETTracker, []); // eslint-disable-line react-hooks/exhaustive-deps

  /*eslint-disable-next-line flights/no-unassigned-todo-comments*/
  return null; // TODO: this can now be turned into a hook...
};

const ClientOnlyTrackers: React.FunctionComponent = () => (
  <NoSSR>
    <Trackers />
  </NoSSR>
);

export default React.memo(ClientOnlyTrackers);
